import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from './axios';

/**
 * Hub shelf transfers: move stock to the front shelf (DEFAULT), to a back shelf, or across
 * shelves. SCM stores the transfer and returns at once; Unicommerce runs it in the background,
 * so callers poll recent() for the outcome.
 *
 * Every method returns the SCM envelope `{ status, data }`. A failed envelope is rejected by
 * the axios interceptor with `status.message` as the error message.
 */
class ShelfTransferApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/wms/shelf-transfer');
  }

  /**
   * SKU details plus the shelves holding it at the facility, for one inventory type.
   * `code` may be a SKU code, barcode or vendor SKU code. Shelf data comes from a 15-min
   * export; `dataAvailable=false` means it could not be read (not "no stock").
   */
  async lookup(facilityId: number, code: string, inventoryType: string) {
    const response = await axiosInstance.get(`${this.baseURL}/lookup`, {
      params: { facilityId, code, inventoryType },
    });
    return response.data;
  }

  /** Creates the transfer (status NEW). Unicommerce runs it after this returns. */
  async createTransfer(payload: {
    facilityId: number;
    type: string;
    inventoryType: string;
    destinationShelf: string;
    items: { skuCode: string; sourceShelf: string; quantity: number }[];
    requestId?: string;
    sessionId?: string;
    pieceNo?: number;
  }) {
    const response = await axiosInstance.post(`${this.baseURL}`, payload);
    return response.data;
  }

  /** Transfers at the facility from the last 24 h, newest first. */
  async recent(facilityId: number) {
    const response = await axiosInstance.get(`${this.baseURL}/recent`, {
      params: { facilityId },
    });
    return response.data;
  }

  /** Resumes a Failed transfer from the step Unicommerce's state implies. */
  async retry(id: number) {
    const response = await axiosInstance.post(`${this.baseURL}/${id}/retry`, {});
    return response.data;
  }

  /** New destination for a transfer whose shelf Unicommerce refused; the transfer then resumes. */
  async setShelf(id: number, shelf: string) {
    const response = await axiosInstance.post(`${this.baseURL}/${id}/destination`, { shelf });
    return response.data;
  }

  /** Puts the units back on their source shelves and completes the putaway there. */
  async cancel(id: number) {
    const response = await axiosInstance.post(`${this.baseURL}/${id}/cancel`, {});
    return response.data;
  }

  /** Undo a transfer done in SCM: every unit goes back to its source shelf. */
  async undo(id: number, undoRequestId: string) {
    const response = await axiosInstance.post(`${this.baseURL}/${id}/undo`, { undoRequestId });
    if (response.data?.status?.type === 'ERROR') {
      throw new Error(response.data.status.message);
    }
    return response.data;
  }
}

export default ShelfTransferApiClient;
